// Module-level dedupe + sessionStorage cache for /api/geo/zip so multiple
// components (Footer card, QuoteForm hero, etc.) share a single network call.
// Backend already caches per-IP for 1 hour; this prevents duplicate calls
// within a single page load and persists across SPA navigation.
import { useEffect, useState } from "react";
import { fetchGeoZip } from "./api";

const SESSION_KEY = "carsavr_geo_v1";
let inFlight = null;

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(data) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    /* private mode / quota — ignore */
  }
}

export function getGeo() {
  const cached = readSession();
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = fetchGeoZip()
    .then((data) => {
      const safe = data || {};
      writeSession(safe);
      return safe;
    })
    .catch(() => ({}))
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// React hook wrapper. Returns { geo, loading } where geo is `null` until resolved.
export function useGeo() {
  const [geo, setGeo] = useState(() => readSession());
  const [loading, setLoading] = useState(() => !readSession());

  useEffect(() => {
    if (geo) return;
    let cancelled = false;
    getGeo().then((data) => {
      if (cancelled) return;
      setGeo(data);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [geo]);

  return { geo: geo || {}, loading };
}
